import { useEffect, useRef, useCallback } from 'react';
import { useMap } from 'react-leaflet';
import { useNetworkElementsStore } from '../../store/networkElementsStore';
import { useNetworkMapStore } from '../../store/networkMapStore';
import { useNetworkUIStore } from '../../store/networkUIStore';
import { useTabStore } from '../../store/tabStore';
import { useZoneStore } from '../../store/zoneStore';
import { AnyNetworkElement, isAbgangsleitung, hasCoordinates } from '../../types/network';
import { calculateMapBounds } from './mapBounds';
import L from 'leaflet';

const DEFAULT_CENTER: [number, number] = [53.340242, 9.885631];
const DEFAULT_ZOOM = 18.5;

/** Check if the map container is actually visible (has dimensions) */
const isMapVisible = (map: L.Map): boolean => {
  const container = map.getContainer();
  return container.offsetWidth > 0 && container.offsetHeight > 0;
};

export const MapViewHandler = () => {
  // Get map view state from networkUIStore
  const mapCenter = useNetworkUIStore(state => state.mapCenter);
  const mapZoom = useNetworkUIStore(state => state.mapZoom);
  const isRightSidebarCollapsed = useNetworkUIStore(state => state.isRightSidebarCollapsed);
  const isControlSidebarCollapsed = useNetworkUIStore(state => state.isControlSidebarCollapsed);
  const activeTabId = useTabStore(state => state.activeTabId);
  const activeTab = useTabStore(state => state.tabs.find(t => t.id === state.activeTabId));
  const activeZoneId = useZoneStore(state => state.activeZoneId);
  const map = useMap();
  const setMapPosition = useNetworkMapStore(state => state.setMapPosition);
  const elements = useNetworkElementsStore(state => state.elements) as AnyNetworkElement[];
  const initialized = useRef(false);
  const lastElements = useRef<AnyNetworkElement[]>([]);
  const lastFitZoneId = useRef<string | null>(null);
  const pendingRefit = useRef(false);

  /** Fit the map to the bounds of the given elements */
  const fitToElements = useCallback((els: AnyNetworkElement[], animate = false) => {
    const points = calculateMapBounds(els);
    if (points.length > 0) {
      const bounds = L.latLngBounds(points);
      const center = bounds.getCenter();
      map.fitBounds(bounds, {
        padding: [100, 100],
        maxZoom: 22,
        animate
      });
      setMapPosition({
        center: [center.lat, center.lng],
        zoom: map.getZoom() || DEFAULT_ZOOM
      });
      return true;
    }
    return false;
  }, [map, setMapPosition]);

  // Reset initialization state when zone changes so bounds are recalculated
  useEffect(() => {
    if (activeZoneId !== lastFitZoneId.current) {
      initialized.current = false;
      lastElements.current = [];
      pendingRefit.current = true;
    }
  }, [activeZoneId]);

  // Invalidate map size when sidebars collapse/expand
  useEffect(() => {
    // Small delay to allow CSS transition to complete
    const timeoutId = setTimeout(() => {
      map.invalidateSize();
    }, 350);

    return () => clearTimeout(timeoutId);
  }, [map, isRightSidebarCollapsed, isControlSidebarCollapsed]);

  // When switching back to map tab: invalidate size and refit if zone changed while hidden
  useEffect(() => {
    if (activeTab?.type === 'map') {
      const timeoutId = setTimeout(() => {
        map.invalidateSize();

        // If zone changed while map was hidden, refit bounds now
        if (pendingRefit.current && elements.length > 0) {
          requestAnimationFrame(() => {
            if (isMapVisible(map)) {
              // A jump target set meanwhile (drill-down from the Netzmonitoring page: zone
              // switch + element focus) wins over the zone fit — read at execution time, the
              // target arrives right after the elements.
              if (useNetworkUIStore.getState().mapZoom == null) {
                fitToElements(elements);
              }
              lastFitZoneId.current = activeZoneId;
              pendingRefit.current = false;
              initialized.current = true;
              lastElements.current = elements;
            }
          });
        }
      }, 50);

      return () => clearTimeout(timeoutId);
    }
  }, [map, activeTabId, activeTab?.type, activeZoneId, elements, fitToElements]);

  // Handle dynamic view updates from search
  useEffect(() => {
    if (mapCenter && mapZoom) {
      // Set a high max zoom to allow zooming in further
      map.setMaxZoom(22);
      // Set the view
      map.setView([mapCenter.lat, mapCenter.lng], mapZoom, { animate: true });
      setMapPosition({
        center: [mapCenter.lat, mapCenter.lng],
        zoom: mapZoom
      });
    }
  }, [map, mapCenter, mapZoom, setMapPosition]);

  // Handle initial bounds calculation and element changes
  useEffect(() => {
    // Skip if already initialized or elements haven't changed, or if we have a specific mapZoom set
    if ((initialized.current && elements === lastElements.current) || mapZoom) {
      return;
    }

    // If map is not visible (hidden tab), defer to the tab-switch handler
    if (!isMapVisible(map)) {
      pendingRefit.current = true;
      return;
    }

    // Check if elements length changed - quick check before deep comparison
    const hasElementsLengthChanged = elements.length !== lastElements.current.length;

    // Only do expensive coordinate check if length hasn't changed
    const hasCoordinateChanges = hasElementsLengthChanged || (() => {
      // Check a sample of elements (first 10) for changes to avoid checking all
      const sampleSize = Math.min(10, elements.length);

      for (let index = 0; index < sampleSize; index++) {
        const element = elements[index];
        const lastElement = lastElements.current[index];

        // If lastElement doesn't exist, consider it a change
        if (!lastElement) return true;

        // Check if coordinates changed
        if (hasCoordinates(element) && hasCoordinates(lastElement)) {
          if (element.coordinates.lat !== lastElement.coordinates.lat ||
              element.coordinates.lng !== lastElement.coordinates.lng) {
            return true;
          }
        }

        // Check if waypoints changed for Abgangsleitung
        if (isAbgangsleitung(element) && isAbgangsleitung(lastElement)) {
          if (element.waypoints.length !== lastElement.waypoints.length) {
            return true;
          }

          // Check only first waypoint as a quick indicator
          if (element.waypoints.length > 0 && lastElement.waypoints.length > 0) {
            const wp = element.waypoints[0];
            const lastWp = lastElement.waypoints[0];
            if (!lastWp || !wp) return true;
            if (wp.lat !== lastWp.lat || wp.lng !== lastWp.lng) {
              return true;
            }
          }
        }
      }

      return false;
    })();

    // Update last known elements
    lastElements.current = elements;

    // Only recalculate bounds if coordinates have changed
    if (!hasCoordinateChanges && initialized.current) {
      return;
    }

    // Calculate bounds
    const animFrameId = requestAnimationFrame(() => {
      if (elements.length > 0) {
        fitToElements(elements);
        lastFitZoneId.current = activeZoneId;
        pendingRefit.current = false;
      } else if (!initialized.current && !activeZoneId) {
        // Default view only when no zone is selected. During a zone switch the elements are
        // empty while the new zone loads — keep the map still until they arrive and fit then.
        map.setView(DEFAULT_CENTER, DEFAULT_ZOOM, { animate: false });
        setMapPosition({
          center: DEFAULT_CENTER,
          zoom: DEFAULT_ZOOM
        });
      }

      initialized.current = true;
    });

    return () => {
      cancelAnimationFrame(animFrameId);
    };
  }, [map, elements, setMapPosition, mapZoom, activeZoneId, fitToElements]);


  return null;
};
